import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { ResultsDrawer } from '../components/ResultsDrawer'
import { useResultsThread } from '../hooks/useResultsThread'

export type ResultsTarget = {
  opportunityId: number
  symbol: string
  name?: string | null
  logoUrl?: string | null
}

type ResultsDrawerContextValue = {
  open: boolean
  target: ResultsTarget | null
  openResultsDrawer: (target: ResultsTarget) => void
  closeResultsDrawer: () => void
}

const ResultsDrawerContext = React.createContext<ResultsDrawerContextValue | undefined>(
  undefined,
)

export function useResultsDrawer() {
  const ctx = React.useContext(ResultsDrawerContext)
  if (!ctx) throw new Error('useResultsDrawer must be used within ResultsDrawerProvider')
  return ctx
}

export function ResultsDrawerProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const [target, setTarget] = React.useState<ResultsTarget | null>(null)

  const { user, profile } = useAuth()
  const currentUserId = user?.id ? String(user.id) : null
  const currentUserName = profile?.full_name || user?.email || 'Usuário'
  const currentUserAvatar = (profile as any)?.avatar_url ?? null
  const isMaster = profile?.role === 'master'

  const threadHook = useResultsThread(
    target ? target.opportunityId : null,
    target
      ? {
          name: currentUserName,
          avatarUrl: currentUserAvatar,
        }
      : null,
    open && !!target,
  )

  const openResultsDrawer = React.useCallback((next: ResultsTarget) => {
    setTarget(next)
    setOpen(true)
  }, [])

  const closeResultsDrawer = React.useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <ResultsDrawerContext.Provider
      value={{
        open,
        target,
        openResultsDrawer,
        closeResultsDrawer,
      }}
    >
      {children}
      {target && (
        <ResultsDrawer
          open={open}
          onOpenChange={setOpen}
          opportunityId={target.opportunityId}
          symbol={target.symbol}
          name={target.name}
          logoUrl={target.logoUrl}
          threadHook={threadHook}
          currentUserId={currentUserId}
          isMaster={!!isMaster}
        />
      )}
    </ResultsDrawerContext.Provider>
  )
}

